import { createApp, nextTick } from "vue";
import ProductItem from "../components/product-item/ProductItem";
import FrameSpec from "@framebridge/toolbox/FrameSpec";

const INITIAL_MOUNT_BATCH_SIZE = 8;
const IDLE_FALLBACK_CHUNK_SIZE = 4;

function requestIdle(callback) {
  if (typeof window.requestIdleCallback === "function") {
    return window.requestIdleCallback(callback, { timeout: 2000 });
  }
  // Safari has no requestIdleCallback; timeRemaining() caps at IDLE_FALLBACK_CHUNK_SIZE calls so the fallback still yields between chunks.
  let remaining = IDLE_FALLBACK_CHUNK_SIZE;
  return setTimeout(() => callback({ didTimeout: false, timeRemaining: () => (remaining-- > 0 ? 1 : 0) }), 1);
}

// Mounting replaces the skeleton, so its Liquid image gets a chance to be painted first and count as the page's LCP.
function whenImagePainted($image) {
  if (document.hidden) {
    return Promise.resolve();
  }

  const decoded = typeof $image.decode === "function" ? $image.decode().catch(() => {}) : Promise.resolve();

  return decoded.then(() => new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  }));
}

function hasUploadedArtworkNow() {
  const uploadedArtwork = localStorage.getItem("uploadedArtwork");
  return uploadedArtwork !== null && uploadedArtwork !== "";
}

function productItemApp(opts) {
  const config = {
    dom: {
      parent: "collection"
    },
    cls: {
      hasQuickview: "has-quickview",
      templateCollection: "template-collection"
    }
  };

  const c = __.extend(config, opts);

  // Globals
  const $productItems = document.querySelectorAll(`[id^="${c.dom.parent}-productItemApp"],[id*=" ${c.dom.parent}-productItemApp"]`);
  const $templateCollection = document.querySelector(`body.${c.cls.templateCollection}`);
  const $parent = document.getElementById(c.dom.parent);
  const hasUploadedArtwork = hasUploadedArtworkNow();
  const parentHasQuickviewClass = $parent ? __.hasClass($parent, c.cls.hasQuickview) : null;

  function init() {
    if (!$productItems || $productItems.length === 0) {return;}
    productItemVueApp();
    updateJsonLd($productItems);
  }

  /**
   * Update JSON-LD
   */
  function updateJsonLd(newProductNodes) {
    if (!$templateCollection) {
      return;
    }

    const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
    let jsonLdData = null;
    let targetScript = null;

    // Find the correct JSON-LD script
    jsonLdScripts.forEach((script) => {
      const data = JSON.parse(script.innerHTML);
      if (data && data.mainEntity && data.mainEntity.itemListElement) {
        jsonLdData = data;
        targetScript = script;
      }
    });

    if (!jsonLdData || !targetScript) {
      console.error("JSON-LD script with mainEntity not found");
      return;
    }

    // Process nodes, skipping the first 12 and any already processed
    newProductNodes.forEach((node, index) => {
      if (index >= 12 && !node.dataset.jsonLdProcessed) {
        const data = node.dataset;
        jsonLdData.mainEntity.itemListElement.push({
          "@type": "ListItem",
          position: jsonLdData.mainEntity.itemListElement.length + 1,
          url: `${window.location.origin}/${data.collectionHandle}/${data.handle}`,
          name: data.productTitle,
          description: data.productSubtitle, // Assuming subtitle is used as description
          image: `https:${data.imageSrc}`
        });

        // Mark this product as processed
        node.dataset.jsonLdProcessed = "true";
      }
    });

    targetScript.innerHTML = JSON.stringify(jsonLdData, null, 2);
  }

  async function productItemVueApp() {
    let allFullMouldings = null;

    if (hasUploadedArtwork) {
      try {
        const joineryHost = SDG?.Data?.joineryHost || "https://staging.framebridge.com";
        const mouldingsResult = await FrameSpec.getAllMouldings(joineryHost);
        allFullMouldings = mouldingsResult.mouldings;
      } catch (error) {
        console.error("Error fetching mouldings for collection page", error);
      }
    }

    function mountProductItem($productItem, priorityImageSrc = null) {
      // A filter/pagination re-render can detach a card before its deferred mount runs; skip it.
      if (!$productItem.isConnected) { return; }

      // Re-read fresh: deferred cards may mount seconds after productItemApp() ran, long enough for artwork to change.
      const cardHasUploadedArtwork = hasUploadedArtworkNow();

      if (cardHasUploadedArtwork) {
        __.addClass($productItem.parentNode, "js-quickview-button");
      }
      // eslint-disable-next-line no-underscore-dangle
      const isMounted = $productItem?.__vue_app__ || false;
      // ensure we don't mount the app after pagination
      if (isMounted) {return;}

      const { id } = $productItem;
      const { productSubtitle, giftCard: giftCardTitle } = $productItem.dataset;
      const { collectionLabelCopy, collectionLabelUrl } = $productItem.dataset;
      let collectionLabel = null;
      if (collectionLabelCopy && collectionLabelUrl) {
        collectionLabel = {
          copy: collectionLabelCopy,
          url: collectionLabelUrl
        };
      }
      const { productBadge, variantBadges } = $productItem.dataset;
      const { productDimensions } = $productItem.dataset;
      const { productValueProps } = $productItem.dataset;
      const { isCollectionPage } = $productItem.dataset;
      const { isSearchPage } = $productItem.dataset;
      const { selectedVariantId } = $productItem.dataset;
      const $sdgScript = $productItem.previousElementSibling;
      const product = $sdgScript
        ? formatData($sdgScript, "product")
        : null;
      const featuredDefaultImage = $sdgScript
        ? formatData($sdgScript, "featuredImage")
        : null;
      const images = $sdgScript
        ? formatData($sdgScript, "image")
        : null;
      const altVariantImagesArray = $sdgScript
        ? formatData($sdgScript, "productCardAssets")
        : null;

      const colorFilterValuesArray = $sdgScript
        ? formatData($sdgScript, "colorFilterValues")
        : null;

      const maskedValuesArray = $sdgScript
        ? formatData($sdgScript, "maskedRendering")
        : null;
      const variantSpecArray = $sdgScript
        ? formatData($sdgScript, "variantSpec")
        : null;
      const comparePanelVariantAssetsArray = $sdgScript
        ? formatData($sdgScript, "comparePanelVariantAssets")
        : null;

      const swatches = $sdgScript
        ? formatData($sdgScript, "swatches")
        : null;

      const parentProduct = $sdgScript
        ? formatData($sdgScript, 'parentProduct')
        : null;

      if (!product) { return; }

      const app = createApp(
        ProductItem,
        {
          product,
          featuredDefaultImage,
          images,
          isQuickview: parentHasQuickviewClass || cardHasUploadedArtwork,
          swatches,
          productSubtitle,
          selectedVariantId,
          giftCardTitle,
          productBadge: productBadge ? JSON.parse(productBadge) : null,
          // Per-variant badge overrides — keyed by variant ID string
          variantBadges: variantBadges ? JSON.parse(variantBadges) : null,
          productDimensions,
          productValueProps,
          isCollectionPage: isCollectionPage === "true",
          isSearchPage: isSearchPage === "true",
          altVariantImagesArray,
          colorFilterValuesArray,
          maskedValuesArray,
          comparePanelVariantAssetsArray,
          variantSpecArray,
          parentProduct,
          collectionLabel,
          allFullMouldings,
          priorityImageSrc,
        }
      );

      app.config.globalProperties.$config = {
        joineryHost: SDG.Data.joineryHost || "https://staging.framebridge.com"
      };

      app.mount(`#${id}`);
    }

    // eslint-disable-next-line no-underscore-dangle
    const unmountedItems = Array.from($productItems).filter(($item) => !$item?.__vue_app__);
    const initialBatch = unmountedItems.slice(0, INITIAL_MOUNT_BATCH_SIZE);
    const deferredItems = unmountedItems.slice(INITIAL_MOUNT_BATCH_SIZE);

    initialBatch.forEach(($item) => {
      const $liquidImage = $item.querySelector(".product-item-skeleton img");

      // shortlist-add-to.js binds hearts that exist at startup and watches for new ones only if it found any, so a lone card can't wait
      if (!$liquidImage || initialBatch.length === 1) {
        mountProductItem($item);
        return;
      }

      whenImagePainted($liquidImage).then(() => mountProductItem($item, $liquidImage.currentSrc || null));
    });
    mountDeferredBatches(deferredItems);

    // re-trigger the carousel's existing resize check once mounted.
    if (initialBatch.length > 0) {
      nextTick(() => {
        document.dispatchEvent(new CustomEvent("resizeFlickity", { detail: { height: 0 } }));
      });
    }

    function mountDeferredBatches(items) {
      if (items.length === 0) { return; }

      requestIdle((deadline) => {
        while (items.length > 0 && (deadline.timeRemaining() > 0 || deadline.didTimeout)) {
          mountProductItem(items.shift());
        }
        if (items.length > 0) {
          mountDeferredBatches(items);
        }
      });
    }
  }

  function formatData($el, type) {
    const dataArray = $el.innerHTML.trim().split("SDG.Data.").filter(String);
    
    // Helper function to extract and format data
    function extractData(searchKey, isArray = false) {
      const filteredArray = dataArray.filter((data) => data.includes(`${searchKey}=`));
      if (!filteredArray?.length) {
        // Return appropriate empty value based on type
        return isArray ? [] : {};
      }
      
      let formattedData = filteredArray[0]
        .replace(`${searchKey}=`, "")
        .replace(isArray ? "];" : "};", isArray ? "]" : "}")
        .trim();
      if (formattedData.endsWith(";")) {
        formattedData = formattedData.replace(";", "");
      }
      
      const parsedData = JSON.parse(formattedData);
      // If this is parentProduct and it's an empty object, return null instead
      if (searchKey === "parentProduct" && Object.keys(parsedData).length === 0) {
        return null;
      }

      // Fix title formatting: ensure space after colons
      if (searchKey === "productJson" && parsedData?.title) {
        parsedData.title = parsedData.title.replace(/:\s*(\S)/g, ": $1");
      }

      return parsedData;
    }

    if ($el && type === "swatches") {
      const jsonData = extractData("swatchArray", true);
      
      if (jsonData?.length > 0) {
        // build the array of objects
        const swatchesFromVariants = jsonData.map((swatch) => {
          const swatchObject = {};
          swatch.split(",").forEach((item) => {
            const [key, value] = item.split(":");
            // replace spaces with underscores
            const keyName = key.replace(/\s/g, "_");

            swatchObject.color = keyName;
            swatchObject.value = value;
          });
          return swatchObject;
        });

        return swatchesFromVariants;
      }
      return [];
    }

    const dataTypes = {
      product: { key: "productJson", isArray: false },
      featuredImage: { key: "featuredImageJson", isArray: false },
      image: { key: "imageJson", isArray: true },
      productCardAssets: { key: "alternativeVariantImages", isArray: true },
      colorFilterValues: { key: "colorFilterValues", isArray: true },
      maskedRendering: { key: "maskedRendering", isArray: true },
      variantSpec: { key: "variantSpec", isArray: true },
      comparePanelVariantAssets: { key: "comparePanelVariantAssets", isArray: true },
      parentProduct: { key: "parentProduct", isArray: false }
    };

    const dataType = dataTypes[type];
    if ($el && dataType) {
      return extractData(dataType.key, dataType.isArray);
    }

    return [];
  }

  init();
}

export default productItemApp;
